import { useParams, Link } from 'react-router-dom';
import { articleDetails } from '@/mocks/articleData';
import { hotRecommendations } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? articleDetails[id] : undefined;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您访问的内容可能已被移除或链接有误。</p>
            <Link to="/" className="whitespace-nowrap inline-flex items-center px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = hotRecommendations
    .filter(item => item.id !== article.id && item.categorySlug === article.categorySlug)
    .slice(0, 3);
  if (relatedArticles.length < 3) {
    const others = hotRecommendations.filter(item => item.id !== article.id && !relatedArticles.includes(item));
    relatedArticles.push(...others.slice(0, 3 - relatedArticles.length));
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: '亚洲视频精选平台',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://axpar.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://axpar.cn/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={[
            { label: '首页', href: '/' },
            { label: article.category, href: `/category/${article.categorySlug}` },
            { label: article.title },
          ]} />
        </div>

        <article className="w-full max-w-[900px] mx-auto px-4 md:px-6 lg:px-8 py-8">
          <header className="mb-8">
            <div className="flex items-center gap-3 flex-wrap mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="whitespace-nowrap px-3 py-1 rounded-full bg-primary-50 text-primary-600 text-sm font-medium hover:bg-primary-100 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-background-100 text-foreground-500 text-sm">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-user-line"></i> {article.authorName}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i> {(article.views / 10000).toFixed(1)}万 阅读
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> {article.duration}
              </span>
            </div>
          </header>

          <div className="relative aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            {article.content.split('\n\n').map((paragraph, idx) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return null;
              if (trimmed.startsWith('## ')) {
                return (
                  <h2 key={idx} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-8 mb-4">
                    {trimmed.replace('## ', '')}
                  </h2>
                );
              }
              if (trimmed.startsWith('### ')) {
                return (
                  <h3 key={idx} className="font-heading text-lg md:text-xl font-semibold text-foreground-900 mt-6 mb-3">
                    {trimmed.replace('### ', '')}
                  </h3>
                );
              }
              return (
                <p key={idx} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                  {trimmed}
                </p>
              );
            })}
          </div>

          <footer className="mt-10 pt-8 border-t border-background-200">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微博">
                <i className="ri-weibo-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="分享到微信">
                <i className="ri-wechat-line text-foreground-600"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 transition-colors cursor-pointer" aria-label="复制链接">
                <i className="ri-link text-foreground-600"></i>
              </button>
            </div>
          </footer>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full max-w-[900px] mx-auto px-4 md:px-6 lg:px-8 pb-16" aria-labelledby="related-title">
            <h2 id="related-title" className="font-heading text-xl font-bold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {relatedArticles.map((item) => (
                <Link
                  key={item.id}
                  to={`/article/${item.id}`}
                  className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-background-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={item.imageUrl}
                      alt={item.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-foreground-400 mt-2">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}